import { useEffect, useState, useCallback } from 'react'
import { api, type Page } from '../api/client'
import type { Company } from '../api/companies'
import { SortableTh } from '../components/SortableTh'
import {
  ButtonLink,
  IconButtonLink,
  Callout,
  Checkbox,
  DataTable,
  EmptyState,
  FilterBar,
  LoadingState,
  Page as PageShell,
  PageHeader,
  Pagination,
  StatusPill,
  TextField,
  type PillTone,
} from '../components/ui'

const STATUS_COLORS: Record<string, PillTone> = {
  active: 'success',
  inactive: 'warning',
  suspended: 'error',
  merged: 'neutral',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Attiva',
  inactive: 'Inattiva',
  suspended: 'Sospesa',
  merged: 'Unita',
}

const PAGE_SIZE = 25

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export function CompaniesPage() {
  // Applied filter state (triggers fetch)
  const [search, setSearch] = useState('')
  const [includeMerged, setIncludeMerged] = useState(false)
  const [page, setPage] = useState(1)
  // F-095: ordinamento server-side (created_desc | created_asc | name_asc | name_desc)
  const [sort, setSort] = useState('created_desc')

  // Pending form state
  const [pendingSearch, setPendingSearch] = useState('')
  const [pendingIncludeMerged, setPendingIncludeMerged] = useState(false)

  const [data, setData] = useState<Page<Company> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const q = new URLSearchParams()
      if (search) q.set('search', search)
      if (includeMerged) q.set('includeMerged', 'true')
      q.set('page', String(page))
      q.set('pageSize', String(PAGE_SIZE))
      q.set('sort', sort)
      const result = await api<Page<Company>>(`/api/companies?${q}`)
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [search, includeMerged, page, sort])

  useEffect(() => {
    void load()
  }, [load])

  function handleSort(next: string) {
    setSort(next)
    setPage(1)
  }

  function handleFilter() {
    setSearch(pendingSearch)
    setIncludeMerged(pendingIncludeMerged)
    setPage(1)
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <PageShell>
      <PageHeader
        title="Aziende"
        icon="business"
        count={total}
        actions={
          <ButtonLink to="/companies/create" variant="filled" icon="add">
            Nuova azienda
          </ButtonLink>
        }
      />

      <FilterBar onSubmit={handleFilter}>
        <TextField
          label="Cerca ragione sociale, P.IVA, CF, email..."
          placeholder="Cerca ragione sociale, P.IVA, CF, email..."
          value={pendingSearch}
          onChange={e => setPendingSearch(e.target.value)}
        />
        <Checkbox
          id="companies-include-merged"
          label="Includi unite"
          checked={pendingIncludeMerged}
          onChange={e => setPendingIncludeMerged(e.target.checked)}
        />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState icon="business" title="Nessuna azienda trovata" />
      ) : (
        <>
          <DataTable
            aria-label="Aziende"
            footer={total > PAGE_SIZE ? <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} /> : undefined}
          >
            <thead>
              <tr>
                <SortableTh label="Ragione Sociale" field="name" sort={sort} onSort={handleSort} />
                <th>P.IVA</th>
                <th>Email</th>
                <th>Telefono</th>
                <th>Stato</th>
                <SortableTh label="Data" field="created" sort={sort} onSort={handleSort} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id}>
                  <td>
                    <a
                      href={`/companies/${c.id}`}
                      className="text-decoration-none md-typescale-emphasized-body-medium"
                    >
                      {c.name}
                    </a>
                  </td>
                  <td>
                    <small>{c.vatNumber ?? '—'}</small>
                  </td>
                  <td>
                    <small>{c.email ?? '—'}</small>
                  </td>
                  <td>
                    <small>{c.phone ?? '—'}</small>
                  </td>
                  <td>
                    <StatusPill tone={STATUS_COLORS[c.status] ?? 'neutral'}>
                      {STATUS_LABELS[c.status] ?? c.status}
                    </StatusPill>
                  </td>
                  <td>{fmtDate(c.createdAt)}</td>
                  <td>
                    <IconButtonLink to={`/companies/${c.id}/edit`} size="sm" icon="edit" aria-label="Modifica" />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </>
      )}
    </PageShell>
  )
}
